import { useState, useEffect } from 'react';
import Modal from './Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import Button from '../Form/Button';
import Form from '../Form/Form';
import Textarea from '../Form/Fields/Textarea';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';
import { useNavigate } from 'react-router-dom';
import { toastCreated } from '../../utils/ToastLink';

import './CreateProductModal.scss';

export default function CreateReturnModal({ order, show, onClose, onSave }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const [remainingMap, setRemainingMap] = useState({});

    useEffect(() => {
        if (!show || !order?.id) return;
        setFetching(true);
        Promise.all([
            axios.get(`${API_BASE_URL}/api/v1/getOrder/${order.id}`, { withCredentials: true }),
            axios.get(`${API_BASE_URL}/api/v1/returns/by-order/${order.id}`, { withCredentials: true })
                .catch(() => ({ data: [] })),
        ]).then(([orderRes, returnsRes]) => {
            const orderItems = orderRes.data.items || [];
            const existingReturns = returnsRes.data || [];

            // Sum already-returned quantities per orderItemId
            const returnedMap = {};
            existingReturns.forEach(ret => {
                (ret.items || []).forEach(ri => {
                    returnedMap[ri.orderItemId] = (returnedMap[ri.orderItemId] || 0) + ri.quantity;
                });
            });

            const remaining = {};
            orderItems.forEach(i => {
                remaining[i.id] = Math.max(0, i.quantity - (returnedMap[i.id] || 0));
            });

            setItems(orderItems);
            setRemainingMap(remaining);
            const initial = {};
            orderItems.forEach(i => { initial[i.id] = 0; });
            setQuantities(initial);
        })
        .catch(() => toast.error('Erreur lors du chargement des articles'))
        .finally(() => setFetching(false));
    }, [show, order?.id]);

    const setQty = (id, val) => {
        const max = remainingMap[id] ?? 0;
        const n = Math.max(0, Math.min(parseInt(val) || 0, max));
        setQuantities(prev => ({ ...prev, [id]: n }));
    };

    const selectedItems = items.filter(i => quantities[i.id] > 0);

    const total = selectedItems.reduce(
        (sum, i) => sum + (quantities[i.id] * (i.unitPrice ?? 0)), 0
    );

    const handleSubmit = async (data) => {
        if (selectedItems.length === 0) {
            toast.warning('Sélectionnez au moins un article à retourner');
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/v1/returns`, {
                orderId: order.id,
                reason: data.reason || '',
                items: selectedItems.map(i => ({ orderItemId: i.id, quantity: quantities[i.id] })),
            }, { withCredentials: true });
            toastCreated(navigate, 'Retour créé avec succès', response.data?.id && `/returns/${response.data.id}`);
            onClose();
            onSave?.();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erreur lors de la création';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const formatAmount = (val) =>
        new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

    return (
        <Modal
            id="createReturnModal"
            title={`Retour — ${order?.reference ?? ''}`}
            show={show}
            onClose={onClose}
            icon={faRotateLeft}
            iconBg="#fef2f2"
            iconColor="#991b1b"
            footer={
                <Button
                    isLoading={loading}
                    loadingMessage="Enregistrement…"
                    className="btn btn-danger w-100"
                    type="submit"
                    form="createReturnForm"
                    disabled={selectedItems.length === 0}
                >
                    Créer le retour{selectedItems.length > 0 && ` (${formatAmount(total)} dhs)`}
                </Button>
            }
        >
            {fetching ? (
                <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>Chargement…</p>
            ) : (
                <Form id="createReturnForm" onSubmit={handleSubmit}>
                    <div className="cdm-section">

                        {/* Items table */}
                        <p className="cdm-section__label">Articles de la vente</p>
                        {items.length === 0 ? (
                            <p style={{ color: '#9ca3af', fontSize: 13 }}>Aucun article trouvé</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={thStyle}>Article</th>
                                        <th style={{ ...thStyle, textAlign: 'center' }}>Commandé</th>
                                        <th style={{ ...thStyle, textAlign: 'center' }}>Restant</th>
                                        <th style={{ ...thStyle, textAlign: 'center' }}>Prix unit.</th>
                                        <th style={{ ...thStyle, textAlign: 'center', width: 90 }}>Retourner</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => {
                                        const remaining = remainingMap[item.id] ?? item.quantity;
                                        const fullyReturned = remaining === 0;
                                        return (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: fullyReturned ? 0.45 : 1 }}>
                                                <td style={tdStyle}>
                                                    <div>
                                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                                            {item.orderable?.name ?? `#${item.id}`}
                                                        </span>
                                                        {fullyReturned && (
                                                            <span style={{ display: 'block', fontSize: 11, color: '#dc2626', fontWeight: 500, marginTop: 2 }}>
                                                                Déjà retourné
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center', fontSize: 13 }}>
                                                    <span style={{ fontWeight: 600, color: fullyReturned ? '#dc2626' : '#059669' }}>
                                                        {remaining}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                                                    {formatAmount(item.unitPrice)}
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={remaining}
                                                        value={quantities[item.id] ?? 0}
                                                        disabled={fullyReturned}
                                                        onChange={e => setQty(item.id, e.target.value)}
                                                        style={qtyInputStyle(quantities[item.id] > 0, fullyReturned)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                    </div>

                    {/* Reason */}
                    <div className="cdm-section" style={{ marginBottom: 0 }}>
                        <p className="cdm-section__label">Motif du retour</p>
                        <Textarea
                            name="reason"
                            placeholder="Précisez la raison du retour (optionnel)"
                        />
                    </div>
                </Form>
            )}
        </Modal>
    );
}

const thStyle = {
    padding: '8px 10px',
    fontSize: 11, fontWeight: 700, color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    textAlign: 'left',
};

const tdStyle = {
    padding: '10px 10px',
};

const qtyInputStyle = (active, disabled) => ({
    width: 64, textAlign: 'center',
    padding: '4px 8px', borderRadius: 6,
    border: `1.5px solid ${active ? '#ef4444' : '#e5e7eb'}`,
    fontSize: 14, fontWeight: 600,
    color: active ? '#ef4444' : '#6b7280',
    outline: 'none',
    background: disabled ? '#f9fafb' : '#fff',
    cursor: disabled ? 'not-allowed' : 'text',
});
